import { useCallback, useMemo, useEffect } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  useReactFlow,
  type OnConnect,
  type Node,
  type Edge,
  MarkerType,
} from '@xyflow/react';

import { PipelineStagesDto, PipelineStageStatus } from '../../types/pipelineStages';
import { nodeTypes } from './nodes';
import { edgeTypes } from './edges';

interface PipelineStagesFlowProps {
  pipelineStages: PipelineStagesDto;
  className?: string;
}

function PipelineStagesFlowInner({ pipelineStages, className = '' }: PipelineStagesFlowProps) {
  // Generate nodes from pipeline stages data
  const generatedNodes = useMemo(() => {
    return pipelineStages.stages.map((stage, index) => {
      // Calculate dynamic spacing based on stage name length
      const baseSpacing = 250;
      const xPosition = index === 0 ? 50 : 
        50 + pipelineStages.stages.slice(0, index).reduce((acc, prevStage) => {
          const prevNameLength = prevStage.displayName?.length || prevStage.name?.length || 10;
          const prevExtraSpacing = Math.max(0, (prevNameLength - 10) * 8);
          return acc + baseSpacing + prevExtraSpacing;
        }, 0);
      
      // Fix status logic: determine correct status based on pipeline progression
      let correctedStatus = stage.status || PipelineStageStatus.NotStarted;
      
      // If this stage is marked as 'skipped' but should be 'waiting' based on pipeline flow
      if (correctedStatus.toLowerCase() === 'skipped') {
        const previousStages = pipelineStages.stages.slice(0, index);
        const lastSucceededIndex = previousStages.reverse().findIndex(s => 
          s.status?.toLowerCase() === 'succeeded'
        );
        
        if (lastSucceededIndex >= 0) {
          const actualLastSucceededIndex = previousStages.length - 1 - lastSucceededIndex;
          // If this is the immediate next stage after a succeeded one, it should be waiting
          if (index === actualLastSucceededIndex + 1) {
            correctedStatus = PipelineStageStatus.Waiting;
          }
          // If it's further ahead, keep as notStarted
          else if (index > actualLastSucceededIndex + 1) {
            correctedStatus = PipelineStageStatus.NotStarted;
          }
        }
      }
      
      return {
        id: stage.id,
        type: 'stageNode',
        position: { 
          x: xPosition, 
          y: 100 
        },
        data: {
          stage: { ...stage, status: correctedStatus },
          label: stage.name,
          status: correctedStatus
        }
      };
    });
  }, [pipelineStages.stages]);

  // Generate edges with arrows connecting sequential stages
  const generatedEdges = useMemo(() => {
    if (pipelineStages.stages.length < 2) {
      return [];
    }
    
    return pipelineStages.stages.slice(0, -1).map((stage, index) => {
      const nextStage = pipelineStages.stages[index + 1];
      return {
        id: `${stage.id}-${nextStage.id}`,
        source: stage.id,
        target: nextStage.id,
        type: 'smoothstep',
        animated: true,
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: '#374151',
          width: 20,
          height: 20,
        },
        style: {
          stroke: '#374151',
          strokeWidth: 2,
        },
      };
    });
  }, [pipelineStages.stages]);

  const [nodes, , onNodesChange] = useNodesState<Node>(generatedNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>(generatedEdges);
  const { fitView } = useReactFlow();
  
  const onConnect: OnConnect = useCallback(
    (connection) => setEdges((edges) => addEdge(connection, edges)),
    [setEdges]
  );

  // Auto-fit the view when nodes change
  useEffect(() => {
    if (nodes.length > 0) {
      const timer = setTimeout(() => {
        fitView({ padding: 0.1, duration: 800 });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [nodes, fitView]);

  return (
    <div className={`w-full h-96 border border-gray-200 rounded-lg overflow-hidden bg-white ${className}`}>
      <ReactFlow
        nodes={nodes}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        edges={edges}
        edgeTypes={edgeTypes}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        fitView
        fitViewOptions={{
          padding: 50,
          includeHiddenNodes: false,
        }}
      >
        <Background gap={20} size={1} />
        <MiniMap />
        <Controls />
      </ReactFlow>
    </div>
  );
}

export default function PipelineStagesFlow(props: PipelineStagesFlowProps) {
  return (
    <ReactFlowProvider>
      <PipelineStagesFlowInner {...props} />
    </ReactFlowProvider>
  );
}